import { DecimalPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { ContextService } from '@core/services/core/context.service';
import { UiHelperService } from '@core/services/core/ui-helper.service';
import { ClientAreaConfigHelperService } from '@core/services/helpers/client-area-config-helper.service';
import { AllFeatureFlags } from '@models/mmobile';
import { PolicyCardConfig } from '@models/policy-config';
import { PolicyLife } from '@models/policy-life';
import { PolicySaving } from '@models/policy-saving';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { FormatMomentDatePipe } from '../../../shared/pipes/format-moment-date.pipe';
import { Policy, POLICY_BASE_TYPES } from '../../models/policy';
import { PolicyPension } from '../../models/policy-pension';
import { LifeCyclePolicyType, ProductCard } from '../../models/products';
import { flat } from '../../utils/utils';
import { MMobileHelperService } from '../core/mmobile-helper.service';
import { NavigationHelper } from '../core/navigation-helper.service';
import { PoliciesHelperService } from '../core/policies-helper.service';

export enum PoliciesTypes {
  CAR,
  HOME,
  HEALTH,
  LIFE,
  SAVING,
  ACCIDENT,
  DEATH,
  OTHER,
  INVESTMENT,
  PENSION,
  LIFE_CYCLE,
  COLLECTIVE_LIFE
}

@Injectable({
  providedIn: 'root'
})
export class ClientAreaHelperService {
  policiesTypes = {
    car: {
      id: PoliciesTypes.CAR,
      name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaAutos',
        'nombre'
      ),
      ro_name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaAutos',
        'nombre_ro'
      ),
      title: null,
      img: 'assets/images/products/car.svg',
      avatar: 'assets/images/products-app/car.svg'
    },
    home: {
      id: PoliciesTypes.HOME,
      name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaHogar',
        'nombre'
      ),
      ro_name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaHogar',
        'nombre_ro'
      ),
      title: null,
      img: 'assets/images/products/home.svg',
      avatar: 'assets/images/products-app/home.svg'
    },
    health: {
      id: PoliciesTypes.HEALTH,
      name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaSalud',
        'nombre'
      ),
      ro_name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaSalud',
        'nombre_ro'
      ),
      title: null,
      img: 'assets/images/products/health.svg',
      avatar: 'assets/images/products-app/health.svg'
    },
    life: {
      id: PoliciesTypes.LIFE,
      name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaVidaRiesgo',
        'nombre'
      ),
      ro_name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaVidaRiesgo',
        'nombre_ro'
      ),
      title: 'CLIENT_AREA.CA_PRODUCT_TYPES.TITLE.LIFE',
      img: 'assets/images/products/life.svg',
      avatar: 'assets/images/products-app/life.svg'
    },
    collective_life: {
      id: PoliciesTypes.COLLECTIVE_LIFE,
      name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaColectivosVida',
        'nombre'
      ),
      ro_name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaColectivosVida',
        'nombre_ro'
      ),
      title: 'CLIENT_AREA.CA_PRODUCT_TYPES.TITLE.COLLECTIVE_LIFE',
      img: 'assets/images/products/collective-life.svg',
      avatar: 'assets/images/products-app/collective-life.svg'
    },
    saving: {
      id: PoliciesTypes.SAVING,
      name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaVidaAhorro',
        'nombre'
      ),
      ro_name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaVidaAhorro',
        'nombre_ro'
      ),
      title: 'CLIENT_AREA.CA_PRODUCT_TYPES.TITLE.SAVING',
      img: 'assets/images/products/savings.svg',
      avatar: 'assets/images/products-app/saving.svg'
    },
    accident: {
      id: PoliciesTypes.ACCIDENT,
      name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaAccidentes',
        'nombre'
      ),
      ro_name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaAccidentes',
        'nombre_ro'
      ),
      title: 'CLIENT_AREA.CA_PRODUCT_TYPES.TITLE.ACCIDENT',
      img: 'assets/images/products/accidents.svg',
      avatar: 'assets/images/products-app/accident.svg'
    },
    death: {
      id: PoliciesTypes.DEATH,
      name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaDecesos',
        'nombre'
      ),
      ro_name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaDecesos',
        'nombre_ro'
      ),
      title: 'CLIENT_AREA.CA_PRODUCT_TYPES.TITLE.DEATH',
      img: 'assets/images/products/death.svg',
      avatar: 'assets/images/products-app/death.svg'
    },
    other: {
      id: PoliciesTypes.OTHER,
      name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaOtros',
        'nombre'
      ),
      ro_name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaOtros',
        'nombre_ro'
      ),
      title: 'CLIENT_AREA.CA_PRODUCT_TYPES.TITLE.OTHER',
      img: 'assets/images/products/other.svg',
      avatar: 'assets/images/products-app/other.svg'
    },
    investment: {
      id: PoliciesTypes.INVESTMENT,
      name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaCarterasInversion',
        'nombre'
      ),
      ro_name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaCarterasInversion',
        'nombre_ro'
      ),
      title: 'CLIENT_AREA.CA_PRODUCT_TYPES.TITLE.INVESTMENT',
      img: 'assets/images/products/investment.svg',
      avatar: 'assets/images/products-app/investment.svg'
    },
    pension: {
      id: PoliciesTypes.PENSION,
      name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaPlanesPensiones',
        'nombre'
      ),
      ro_name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaPlanesPensiones',
        'nombre_ro'
      ),
      title: null,
      img: 'assets/images/products/pension-plan.svg',
      avatar: 'assets/images/products-app/pension.svg'
    },
    lifeCycleFutureProgram: {
      id: PoliciesTypes.LIFE_CYCLE,
      name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaCicloVida',
        'nombre'
      ),
      ro_name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaCicloVida',
        'nombre_ro'
      ),
      title: 'CLIENT_AREA.CA_PRODUCT_TYPES.TITLE.LIFECYCLE_FUTURE_PROGRAM',
      img: 'assets/images/products/pension-plan.svg',
      avatar: 'assets/images/products-app/pension.svg'
    },
    lifeCycleEPSV: {
      id: PoliciesTypes.LIFE_CYCLE,
      name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaCicloVida',
        'nombre'
      ),
      ro_name: this.mmobileHelper.getCustomConfigData(
        'tarjetas',
        'tarjetaCicloVida',
        'nombre_ro'
      ),
      title: 'CLIENT_AREA.CA_PRODUCT_TYPES.TITLE.LIFECYCLE_EPSV',
      img: 'assets/images/products/pension-plan.svg',
      avatar: 'assets/images/products-app/pension.svg'
    }
  };

  constructor(
    private nav: NavigationHelper,
    private formatDatePipe: FormatMomentDatePipe,
    private decimalPipe: DecimalPipe,
    private ui: UiHelperService,
    private policiesHelper: PoliciesHelperService,
    private mmobileHelper: MMobileHelperService,
    private trans: TranslateService,
    private ctx: ContextService,
    private clientAreaConfigHelperService: ClientAreaConfigHelperService
  ) {}

  navToPolicyDetail(
    policy?: Policy,
    category?: ProductCard,
    qparams?,
    payload?
  ) {
    if (category) {
      const policyState = { policy: category, isLifeCycle: true };
      const params = this.getPolicyParams(undefined, category);
      const queryParams = { ...params.queryParams, ...qparams };
      const state = { ...payload, ...policyState };
      this.nav.toNew(['client-area/product-resume', params.id], {
        queryParams,
        state
      });
    } else {
      const policyState = { policy };
      const params = this.getPolicyParams(policy);
      const queryParams = { ...params.queryParams, ...qparams };
      const state = { ...payload, ...policyState };
      this.nav.toNew(['client-area/product-resume', params.id], {
        queryParams,
        state
      });
    }
  }

  getPolicyParams(policy: Policy, category?: ProductCard) {
    let queryParams: any = {};
    let id = '';
    if (category) {
      queryParams = { policy: (category.lifeCyclePolicyType || 'CVID') };
      id = 'pension';
    } else {
      id = policy.policyBaseType;
      switch (policy.policyBaseType) {
        case 'car':
          queryParams = { policyNumber: policy.policyNumber };
          break;
        case 'home':
        case 'other':
          queryParams = {
            policyNumber: policy.policyNumber,
            type: policy.type
          };
          break;
        case 'life':
        case 'collective-life':
          queryParams = {
            policyNumber: policy.policyNumber,
            subpolicyNumber: policy.subpolicyNumber,
            type: policy.type
          };
          break;
        case 'collective-life':
          queryParams = {
            policyNumber: policy.policyNumber,
            subpolicyNumber: policy.subpolicyNumber,
            type: policy.type
          };
          break;
        case 'saving':
          queryParams = {
            policyNumber: policy.policyNumber,
            subpolicyNumber: policy.subpolicyNumber,
            type: policy.type
          };
          break;
        case 'health':
          queryParams = {
            policyNumber: policy.policyNumber,
            figure: policy.figure
          };
          break;
        case 'pension':
          queryParams = {
            policyNumber: policy.policyNumber,
            productCode: policy.productCode
          };
          break;
        case 'investment':
        case 'investment-nova':
        case 'accident':
        case 'death':
          queryParams = { policyNumber: policy.policyNumber };
          break;
        default:
      }
    }
    return { id, queryParams };
  }


  getLifeCyclePoliciesBasedOnType(policies: PolicyPension[], lifeCycleType: LifeCyclePolicyType) {
    if (policies.length === 0 || !lifeCycleType) { return []; }
    if (lifeCycleType === LifeCyclePolicyType.FUTURE_PROGRAM) {
      return policies.filter((policy: PolicyPension) => this.policiesHelper.islifeCycleFutureProgramPolicy(policy));
    }
    if (lifeCycleType === LifeCyclePolicyType.EPSV) {
      return policies.filter((policy: PolicyPension) => this.policiesHelper.isLifeCycleEPSVPolicies(policy));
    }
  }

  getLifeCyclePoliciesType(policies: PolicyPension[]) {
    const areLifeCycleEPSVPolicies = policies.every((policy: PolicyPension) => this.policiesHelper.isLifeCycleEPSVPolicies(policy));
    return areLifeCycleEPSVPolicies ? LifeCyclePolicyType.EPSV : LifeCyclePolicyType.FUTURE_PROGRAM;
  }

  getProductCardFromLifeCyclePolicies(policies: PolicyPension[], lifeCyclePolicyType: LifeCyclePolicyType) {
    const currentDate = this.getMostCurrentDate(policies);
    const lifeCycleImport = policies.reduce((acc, policy) => acc + parseFloat(policy.contractImport), 0);

    return {
      img: lifeCyclePolicyType === LifeCyclePolicyType.EPSV ?
        this.policiesTypes.lifeCycleEPSV.img : this.policiesTypes.lifeCycleFutureProgram.img,
      avatar: lifeCyclePolicyType === LifeCyclePolicyType.EPSV ?
        this.policiesTypes.lifeCycleEPSV.avatar : this.policiesTypes.lifeCycleFutureProgram.avatar,
      policyType: lifeCyclePolicyType === LifeCyclePolicyType.EPSV ?
        this.policiesTypes.lifeCycleEPSV.id : this.policiesTypes.lifeCycleFutureProgram.id,
      cardTypeString: lifeCyclePolicyType === LifeCyclePolicyType.EPSV ?
        this.policiesTypes.lifeCycleEPSV.name : this.policiesTypes.lifeCycleFutureProgram.name,
      title: lifeCyclePolicyType === LifeCyclePolicyType.EPSV ?
      this.trans.instant(this.policiesTypes.lifeCycleEPSV.title) : this.trans.instant(this.policiesTypes.lifeCycleFutureProgram.title),
      subtitle: `${this.decimalPipe.transform(
        lifeCycleImport,
        '1.2-2',
        'es'
      )} €`,
      secondDetail: this.formatDatePipe.transform(currentDate),
      isCategory: true,
      hasAlert: policies.some(policy => this.policyHasAdvice(policy)),
      belongsToLifeCycle: true,
      policies,
      lifeCyclePolicyType
    } as ProductCard;
  }


  createCategoryCard(policies: Policy[], belongsToLifeCycle?: boolean) {
    if (belongsToLifeCycle) {
      const lifeCyclePoliciesType: LifeCyclePolicyType = this.getLifeCyclePoliciesType(policies as PolicyPension[]);
      return this.getProductCardFromLifeCyclePolicies(policies as PolicyPension[], lifeCyclePoliciesType );
    } else {
      const policyVar = this.getPolicyTypeVars(policies[0].policyBaseType);
      const chipText = `${this.getNumberOfPolicies(policies)} Pólizas`;
      return {
        img: policyVar.img,
        avatar: policyVar.avatar,
        policyType: policyVar.id,
        cardTypeString: this.trans.instant(
          'CLIENT_AREA.CA_PRODUCT_TYPES.CATEGORY'
        ),
        title: policyVar.name ? policyVar.name : this.trans.instant(policyVar.title),
        isCategory: true,
        hasAlert: policies.some(policy => this.policyHasAdvice(policy)),
        chipText,
        belongsToLifeCycle,
        policies
      } as ProductCard;
    }
  }

  createPolicyCard(policy: Policy, isPolicyListPage?: boolean) {
    // Main Identification -> title + endTitle
    // Secondary Identification secundaria -> subtitle
    switch (policy.policyBaseType) {
      case 'car':
        return {
          img: this.policiesTypes.car.img,
          avatar: this.policiesTypes.car.avatar,
          policyType: this.policiesTypes.car.id,
          cardTypeString: this.policiesTypes.car.name,
          cardTypeStringRo: this.policiesTypes.car.ro_name,
          title: policy.vehicle,
          endTitle: policy.plateNumber,
          subtitle: policy.product,
          firstDetail: policy.policyNumber ? policy.policyNumber : null,
          secondDetail: policy.expirationDate
            ? this.formatDatePipe.transform(policy.expirationDate)
            : null,
          isCategory: false,
          hasAlert: this.policyHasAdvice(policy),
          policy
        } as ProductCard;
      case 'home':
        return {
          img: this.policiesTypes.home.img,
          avatar: this.policiesTypes.home.avatar,
          policyType: this.policiesTypes.home.id,
          cardTypeString: this.policiesTypes.home.name,
          cardTypeStringRo: this.policiesTypes.home.ro_name,
          title: policy.address.rawAddress,
          subtitle: policy.product,
          firstDetail: policy.policyNumber ? policy.policyNumber : null,
          secondDetail: policy.expirationDate
            ? this.formatDatePipe.transform(policy.expirationDate)
            : null,
          isCategory: false,
          hasAlert: this.policyHasAdvice(policy),
          policy
        } as ProductCard;
      case 'health':
        return {
          img: this.policiesTypes.health.img,
          avatar: this.policiesTypes.health.avatar,
          policyType: this.policiesTypes.health.id,
          cardTypeString: this.policiesTypes.health.name,
          cardTypeStringRo: this.policiesTypes.health.ro_name,
          title: policy.product,
          subtitle: policy.figure,
          firstDetail: policy.policyNumber ? policy.policyNumber : null,
          secondDetail: policy.expirationDate
            ? this.formatDatePipe.transform(policy.expirationDate)
            : null,
          isCategory: false,
          hasAlert: this.policyHasAdvice(policy),
          policy
        } as ProductCard;
      case 'accident':
        return {
          img: this.policiesTypes.accident.img,
          avatar: this.policiesTypes.accident.avatar,
          policyType: this.policiesTypes.accident.id,
          cardTypeString: this.policiesTypes.accident.name,
          cardTypeStringRo: this.policiesTypes.accident.ro_name,
          title: this.trans.instant(this.policiesTypes.accident.title),
          subtitle: policy.product,
          firstDetail: policy.policyNumber ? policy.policyNumber : null,
          secondDetail: policy.expirationDate
            ? this.formatDatePipe.transform(policy.expirationDate)
            : null,
          isCategory: false,
          hasAlert: this.policyHasAdvice(policy),
          policy
        } as ProductCard;
      case 'death':
        return {
          img: this.policiesTypes.death.img,
          avatar: this.policiesTypes.death.avatar,
          policyType: this.policiesTypes.death.id,
          cardTypeString: this.policiesTypes.death.name,
          cardTypeStringRo: this.policiesTypes.death.ro_name,
          title: this.trans.instant(this.policiesTypes.death.title),
          subtitle: policy.product,
          firstDetail: policy.policyNumber ? policy.policyNumber : null,
          secondDetail: policy.expirationDate
            ? this.formatDatePipe.transform(policy.expirationDate)
            : null,
          isCategory: false,
          hasAlert: this.policyHasAdvice(policy),
          policy
        } as ProductCard;
      case 'other':
        return {
          img: this.policiesTypes.other.img,
          avatar: this.policiesTypes.other.avatar,
          policyType: this.policiesTypes.other.id,
          cardTypeString: this.policiesTypes.other.name,
          cardTypeStringRo: this.policiesTypes.other.ro_name,
          title: isPolicyListPage
            ? policy.product
            : this.trans.instant(this.policiesTypes.other.title),
          subtitle: policy.product,
          firstDetail: policy.policyNumber ? policy.policyNumber : null,
          secondDetail: policy.expirationDate
            ? this.formatDatePipe.transform(policy.expirationDate)
            : null,
          isCategory: false,
          hasAlert: this.policyHasAdvice(policy),
          policy
        } as ProductCard;
      case 'life':
        return {
          img: this.policiesTypes.life.img,
          avatar: this.policiesTypes.life.avatar,
          policyType: this.policiesTypes.life.id,
          cardTypeString: this.policiesTypes.life.name,
          cardTypeStringRo: this.policiesTypes.life.ro_name,
          title: this.trans.instant(this.policiesTypes.life.title),
          subtitle: policy.product,
          firstDetail: policy.policyNumber ? policy.policyNumber : null,
          secondDetail: policy.expirationDate
            ? this.formatDatePipe.transform(policy.expirationDate)
            : null,
          isCategory: false,
          hasAlert: this.policyHasAdvice(policy),
          policy
        } as ProductCard;
      case 'collective-life':
        return {
          img: this.policiesTypes.collective_life.img,
          avatar: this.policiesTypes.collective_life.avatar,
          policyType: this.policiesTypes.collective_life.id,
          cardTypeString: this.policiesTypes.collective_life.name,
          cardTypeStringRo: this.policiesTypes.collective_life.ro_name,
          title: isPolicyListPage
            ? policy.productContract
            : this.trans.instant(
                'CLIENT_AREA.CA_PRODUCT_TYPES.TITLE.COLLECTIVE_LIFE'
              ),
          subtitle: policy.product ? policy.product : policy.productContract,
          firstDetail: policy.policyNumber
            ? policy.policyNumber + policy.subpolicyNumber
            : null,
          secondDetail: policy.expirationDate
            ? this.formatDatePipe.transform(policy.expirationDate)
            : null,
          isCategory: false,
          hasAlert: this.policyHasAdvice(policy),
          policy
        } as ProductCard;
      case 'saving':
        return {
          img: this.policiesTypes.saving.img,
          avatar: this.policiesTypes.saving.avatar,
          policyType: this.policiesTypes.saving.id,
          cardTypeString: this.policiesTypes.saving.name,
          cardTypeStringRo: this.policiesTypes.saving.ro_name,
          title: this.trans.instant(this.policiesTypes.saving.title),
          subtitle: policy.product,
          firstDetail: policy.policyNumber ? policy.policyNumber : null,
          secondDetail: policy.expirationDate
            ? this.formatDatePipe.transform(policy.expirationDate)
            : null,
          isCategory: false,
          hasAlert: this.policyHasAdvice(policy),
          policy
        } as ProductCard;
      case 'investment':
      case 'investment-nova':
        return {
          img: this.policiesTypes.investment.img,
          avatar: this.policiesTypes.investment.avatar,
          policyType: this.policiesTypes.investment.id,
          cardTypeString: this.policiesTypes.investment.name,
          cardTypeStringRo: this.policiesTypes.investment.ro_name,
          title: policy.productDescription,
          subtitle: `${
            policy.contractImport
              ? this.decimalPipe.transform(policy.contractImport, '1.2-2', 'es')
              : '0,00'
          } €`,
          firstDetail: policy.policyNumber ? policy.policyNumber : null,
          secondDetail: policy.contractStartDate
            ? this.formatDatePipe.transform(policy.contractStartDate)
            : null,
          isCategory: false,
          hasAlert: this.policyHasAdvice(policy),
          policy
        } as ProductCard;
      case 'pension':
        return {
          img: this.policiesTypes.pension.img,
          avatar: this.policiesTypes.pension.avatar,
          policyType: this.policiesTypes.pension.id,
          cardTypeString: this.policiesTypes.pension.name,
          cardTypeStringRo: this.policiesTypes.pension.ro_name,
          title: policy.productDescription,
          subtitle: `${
            policy.contractImport
              ? this.decimalPipe.transform(policy.contractImport, '1.2-2', 'es')
              : '0,00'
          } €`,
          firstDetail: policy.policyNumber ? policy.policyNumber : null,
          secondDetail: policy.contractStartDate
            ? this.formatDatePipe.transform(policy.contractStartDate)
            : null,
          isCategory: false,
          hasAlert: this.policyHasAdvice(policy),
          policy
        } as ProductCard;
      default:
    }
  }

 getPolicyTypeVars(policyType) {
    switch (policyType) {
      case 'car':
        return this.policiesTypes.car;
      case 'home':
        return this.policiesTypes.home;
      case 'life':
        return this.policiesTypes.life;
      case 'collective-life':
        return this.policiesTypes.collective_life;
      case 'saving':
        return this.policiesTypes.saving;
      case 'health':
        return this.policiesTypes.health;
      case 'investment':
      case 'investment-nova':
        return this.policiesTypes.investment;
      case 'pension':
        return this.policiesTypes.pension;
      case 'accident':
        return this.policiesTypes.accident;
      case 'death':
        return this.policiesTypes.death;
      case 'other':
        return this.policiesTypes.other;
      default:
    }
  }

  async obtainProductCards(): Promise<{ [id: string]: ProductCard }> {
    const policies$ = POLICY_BASE_TYPES.map(type =>
      this.policiesHelper.getPolicies(type).pipe(catchError(() => of([])))
    );
    const policies = await forkJoin(policies$).toPromise();

    const res = {};
    flat(policies).forEach((policy: Policy) => {
      const productCard = this.createPolicyCard(policy, false);
      res[policy.policyNumber] = productCard;
    });

    return res;
  }

  obtainBusinessPoliciesCardsConfig() {
    return this.clientAreaConfigHelperService.getUniquePolicyBaseTypesInCardsConfig()
    .pipe(switchMap(policyBaseTypes => {
      const policies$ = policyBaseTypes.map(type =>
        this.policiesHelper.getPoliciesByPolicyBaseType(type).pipe(catchError(() => of([])))
      );
      return forkJoin(policies$).pipe(map(policies => {
          const res: {[id: string]: PolicyCardConfig} = {};
          flat(policies).forEach((policy: Policy) => {
            res[policy?.policyNumber] = policy?.configuration?.cardConfiguration as PolicyCardConfig;
          });
          return res;
        }
      ));
    }), catchError(() => of({})));
  }

  isFirstHalfOfYear() {
    const date = new Date();
    return date.getMonth() <= 5;
  }

  isSecondTrimester() {
    const date = new Date();
    return date.getMonth() >= 3 && date.getMonth() <= 5;
  }

  isFiscalDocAvailable(policy: any) {
    return this.isSecondTrimester() && policy?.policyBaseType !== 'life';
  }

  isBonusDocAvailable(policy) {
    return this.isFirstHalfOfYear() && policy.bonds;
  }

  showAlertOk(numRef = '', config?) {
    const data =  [
      { type: 'image', value: 'assets/images/families/success_check.svg' },
      { type: 'title', value: 'Tu solicitud de cambio de datos ha sido enviada con éxito' },
      { type: 'numRef', value: numRef },
      { type: 'info', value: config?.info ? config?.info : ['El cambio solicitado se realizará en un plazo máximo de 48 horas hábiles'] }
    ];
    const buttons = [{ text: this.trans.instant('Entendido'), handler: () => { } }];
    this.ui.buildAlertWithParameters({
      data,
      mode: 'md',
      backdropDismiss: false,
      cssClass: ['custom-one-button-alert'],
      buttons,
      analytics: undefined
    });
  }

  showAlertKo() {
    const data = [
      { type: 'image', value: 'assets/images/families/cross_error.svg' },
      { type: 'title', value: '¡Ups, parece que no ha sido posible realizar tu solicitud!' },
      { type: 'description',  value: 'Por favor inténtalo de nuevo más tarde' }
    ];
    const buttons = [{ text: this.trans.instant('Entendido'), handler: () => { } }];
    this.ui.buildAlertWithParameters({
      data,
      mode: 'md',
      backdropDismiss: false,
      cssClass: ['custom-one-button-alert'],
      buttons,
      analytics: undefined
    });
  }

  buildResidenceInString(policy) {
    return `${ policy.address.streetType }
      ${ policy.address.street }
      ${ policy.address.number }
      ${ policy.address.portal ?? '' }
      ${ policy.address.stair ?? '' }
      ${ policy.address.floor ?? '' }
      ${ policy.address.door  ?? '' }
      ${ policy.address.additionalInfo ?? '' }
      ${ policy.address.city }
      ${ policy.address.postalCode }
      ${ policy.address.province }
      ${ policy.address.countryName }`;
  }

  mapResidence(policy) {
    return {
      viaType: policy.address.streetType,
      viaName: policy.address.street,
      viaNumber: policy.address.number,
      portal: policy.address.portal,
      stairs: policy.address.stair,
      flat: policy.address.floor,
      door: policy.address.door,
      complement: policy.address.additionalInfo,
      location: policy.address.city,
      postalCode: policy.address.postalCode,
      province: policy.address.province,
      country: policy.address.countryName,
      countryCode: policy.address.country,
    };
  }

  private policyHasAdvice(policy) {
    switch (policy.policyBaseType) {
      case 'car':
        return this.isCarPolicyAdvice(policy);
      case 'life':
        return this.isLifePolicyAdvice(policy);
      case 'saving':
        return this.isSavingPolicyAdvice(policy);
      default:
        return policy.pendingReceipts;
    }
  }

  private isCarPolicyAdvice(policy) {
    return (
      policy.pendingReceipts ||
      (policy.hasSuperbonus &&
        policy.superbonusData &&
        policy.superbonusData.statusCode === 'CG')
    );
  }

  private isLifePolicyAdvice(policy) {
    const isMmobileSavingLifeAdvices = this.mmobileHelper.checkIsFeatureEnabled(
      AllFeatureFlags.SAVING_LIFE_ADVICES
    );
    return (
      isMmobileSavingLifeAdvices &&
      (this.isFiscalDocAvailable(policy) ||
        this.checkGenericSavinfLifeAdvicesAvailable(policy))
    );
  }

  private isSavingPolicyAdvice(policy) {
    const isMmobileSavingLifeAdvices = this.mmobileHelper.checkIsFeatureEnabled(
      AllFeatureFlags.SAVING_LIFE_ADVICES
    );
    return (
      isMmobileSavingLifeAdvices &&
      (this.isBonusDocAvailable(policy) ||
        this.isFiscalDocAvailable(policy) ||
        this.checkGenericSavinfLifeAdvicesAvailable(policy))
    );
  }

  private checkGenericSavinfLifeAdvicesAvailable(
    policy: PolicyLife | PolicySaving
  ) {
    return (
      policy.pendingReceipts || policy.expirationProcess || policy.rescueProcess
    );
  }

  private getMostCurrentDate(policies: Policy[]) {
    let currentDate;
    policies.forEach((policy: PolicyPension) => {
      if (currentDate < policy.contractStartDate || currentDate === undefined) {
        currentDate = policy.contractStartDate;
      }
    });
    return currentDate;
  }

  private getNumberOfPolicies(policies: Policy[]) {
    if (policies[0].policyBaseType !== 'pension') {
      return policies.length;
    } else {
      const lifeCycle = policies.filter(
        (policy: PolicyPension) => policy.belongsToLifeCycle
      ) as PolicyPension[];
      if (lifeCycle && lifeCycle.length > 1) {
        return policies.length - lifeCycle.length + 1;
      } else {
        return policies.length;
      }
    }
  }

  getQueryParamsAndState(policy) {
    const policyState = { policy };
    const params = this.getPolicyParams(policy);
    const queryParams = { ...params.queryParams };
    const state = { ...policyState };
    return { queryParams, state };
  }
}
